/* eslint-disable array-callback-return */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useState, useEffect } from 'react';
import {
    Button, Box, Typography, TextField, FormControl,
    FormControlLabel, RadioGroup, Radio, Checkbox
} from "@mui/material";
import { Row, Col, Space, Image } from "antd";
import clsx from "clsx";
import { toast } from 'react-toastify';
import { PrimaryDrawer } from "../../../../../Shared/components/Drawer/PrimaryDrawer";
import { AutoCompleteComponent } from "../../../../../Shared/components/AutoCompleteComponent";
import CustomAlert from "../../../../../Shared/components/CustomAlert";
import { SelectedIcon, UnSelectedIcon } from "../../../../../Shared/components/Icons/selectedIcon";
import SearchIcon from "../../../../../../assets/images/icon_actions/Search Icon.svg";
import customStyles from "../../../../../../assets/styles/customStyles";
import { emptyValidation, customNumberValidationQuantity } from "../../../../../../helpers/validation";
import { dataTypeConstant } from '../../../../../../config/constant';

var XLSX = require("xlsx");

// Must match the dataTypeConstant id in config/constant.js so Export -> edit in Excel -> Import round-trips.
const IMPORT_DATA_TYPE = 'Adjust Stock Lines';

// Only the two columns the import cannot work without are enforced, so a wrong file is still caught
// on upload while every other template column stays optional. That keeps a "Required Fields" download
// importable as-is, and keeps files exported before the batch columns existed importable too. These
// must stay in step with the required: true flags on the dataTypeConstant fieldList.
const REQUIRED_HEADERS = ['Product ID', 'Quantity'];
const BATCH_APPLICABLE_HEADER = 'Batch Applicable';
const BATCH_NUMBER_HEADER = 'Batch Number';

const IMPORT_TYPES = [
    { id: "update", name: "Merge Into Existing Lines" },
    { id: "insert", name: "Insert Only New Products, Do not Update" }
];

const FILE_FORMATS = [
    { id: "xlsx", name: "Microsoft Excel (xls)" },
    { id: "csv", name: "CSV" }
];

export const ImportLines = (props) => {
    const { visible, handleClose, importLines, exportLines } = props;
    const classes = customStyles();
    const fileInput = useRef(null);

    const [form, setForm] = useState({ importType: 'update', fileFormat: 'xlsx', column: 'required_fields' });
    const [selected, setSelected] = useState([]);
    const [filePath, setFilePath] = useState('');
    const [parsed, setParsed] = useState(false);
    const [parseError, setParseError] = useState('');
    const [valid, setValid] = useState([]);
    const [rejected, setRejected] = useState([]);
    const [importLog, setImportLog] = useState(null);

    const fieldList = (dataTypeConstant.find(el => el.id === IMPORT_DATA_TYPE) || {}).fieldList || [];

    useEffect(() => {
        requiredFields(true);
    }, [visible]);

    // Mirrors the shared ImportExport drawer: "Required Fields" narrows the download to the columns
    // the import cannot work without, "All Selected Fields" ticks the whole template.
    const requiredFields = (required) => {
        const keys = fieldList.filter(el => required ? el.required : true).map(el => el.key);
        setSelected(keys);
    };

    const customObject = (details, id) => {
        const a = details && details.filter(el => el.id === id);
        if (a && a?.length !== 0) {
            return a[0];
        }
        return [];
    };

    const changeHandler = (e, name) => {
        setForm({ ...form, [name]: e });
    };

    const handleDataColumn = (e) => {
        const v = e.target.value;
        requiredFields(v === 'required_fields');
        setForm({ ...form, column: v });
    };

    const handleChange = (e) => {
        const value = e.target.value;
        setSelected(prev => prev.indexOf(value) !== -1 ? prev.filter(el => el !== value) : [...prev, value]);
    };

    const resetImport = () => {
        setFilePath('');
        setImportLog(null);
        setParsed(false);
        setParseError('');
        setValid([]);
        setRejected([]);
        if (fileInput.current) {
            fileInput.current.value = '';
        }
    };

    // Sheet booleans arrive as real booleans, 'TRUE'/'FALSE' text, Yes/No or 1/0 depending on how the
    // cell was authored - normalise all of them. Blank returns null so the product's own
    // batchActivation stays the default rather than being forced off.
    const parseBoolean = (value) => {
        if (value === true || value === false) return value;
        if (value === '' || value === undefined || value === null) return null;
        const v = String(value).trim().toLowerCase();
        if (['true', 'yes', 'y', '1'].indexOf(v) !== -1) return true;
        if (['false', 'no', 'n', '0'].indexOf(v) !== -1) return false;
        return null;
    };

    // Only structural validation happens here (required fields, numeric quantity, batch number
    // present when the line is batch applicable). Product existence, unit/location matching, merging
    // duplicates and building batchDetails all happen in CustomForm's importLines() once the user
    // presses Upload File - lines are never touched before that.
    const validateRows = (rows) => {
        const validRows = [];
        const rejectedRows = [];

        rows?.map((row) => {
            const rowNumber = parseInt(row.__rowNum__ || 0) + 1;
            const emptyProduct = emptyValidation('Product ID', row['Product ID']);
            const emptyQuantity = customNumberValidationQuantity('Quantity', row['Quantity']);
            const batchOverride = parseBoolean(row[BATCH_APPLICABLE_HEADER]);
            const batchNumber = row[BATCH_NUMBER_HEADER] ? String(row[BATCH_NUMBER_HEADER]).trim() : '';

            if (!emptyProduct.isValid) {
                rejectedRows.push({ rowNumber, reason: emptyProduct.errors });
            } else if (!emptyQuantity.isValid) {
                rejectedRows.push({ rowNumber, reason: emptyQuantity.errors });
            } else if (batchOverride === true && !batchNumber) {
                rejectedRows.push({ rowNumber, reason: 'Batch Number cannot be blank when Batch Applicable is Yes' });
            } else if (batchNumber.indexOf(',') !== -1) {
                // A row carries a single batch, whose quantity is the row quantity. Multiple batches
                // are expressed as repeat rows for the same product - importLines merges the line
                // quantity and appends each batch.
                rejectedRows.push({ rowNumber, reason: 'Multiple batch numbers in one cell - add one row per batch number' });
            } else {
                validRows.push({
                    rowNumber,
                    productId: String(row['Product ID']).trim(),
                    quantity: parseFloat(row['Quantity']),
                    pricePerUnit: row['Cost Price'] !== '' && row['Cost Price'] !== undefined && row['Cost Price'] !== null ? parseFloat(row['Cost Price']) : null,
                    unitName: row['Unit'] ? String(row['Unit']).trim() : '',
                    locationName: row['Location'] ? String(row['Location']).trim() : '',
                    mrp: row['MRP'] !== '' && row['MRP'] !== undefined && row['MRP'] !== null ? parseFloat(row['MRP']) : null,
                    batchOverride,
                    batchNumber,
                });
            }
        });

        return { validRows, rejectedRows };
    };

    const handleFile = (e) => {
        const files = e.target.files;
        const path = e.target.value;
        if (!files || !files.length) return;

        const file = files[0];
        const allowedExtensions = form.fileFormat === 'csv' ? /(\.csv)$/i : /(\.xlsx|\.xls)$/i;
        if (!allowedExtensions.exec(path)) {
            resetImport();
            setParseError(`Please upload file having extensions ${form.fileFormat} only.`);
            return;
        }

        const reader = new FileReader();
        const rABS = !!reader.readAsBinaryString;
        reader.onload = (ev) => {
            const bstr = ev.target.result;
            const wb = XLSX.read(bstr, { type: rABS ? "binary" : "array", cellDates: true });
            const wsname = wb.SheetNames[0];
            const ws = wb.Sheets[wsname];
            const headerRow = XLSX.utils.sheet_to_json(ws, { header: 1 });
            const result = XLSX.utils.sheet_to_json(ws, { defval: '', raw: true });

            // Validate headers against the columns the import reads, instead of the ImportExport-style
            // `dataResult[0][0] !== duplicate` check - that constant declares duplicate: 'Unit' while
            // the actual first exported header is 'Line Number', so that check would reject every
            // valid export.
            const actualHeaders = (headerRow && headerRow[0]) || [];
            const missingHeaders = REQUIRED_HEADERS.filter(h => actualHeaders.indexOf(h) === -1);

            if (missingHeaders.length > 0) {
                setParseError(`Invalid data format, file not uploaded. Missing column(s): ${missingHeaders.join(', ')}. Please check the template and upload data again`);
                setValid([]);
                setRejected([]);
                setParsed(false);
                return;
            }

            const { validRows, rejectedRows } = validateRows(result);
            setValid(validRows);
            setRejected(rejectedRows);
            setParseError('');
            setParsed(true);
        };

        setFilePath(file.name);
        if (rABS) {
            reader.readAsBinaryString(file);
        } else {
            reader.readAsArrayBuffer(file);
        }
    };

    const exportFile = (type) => {
        exportLines(type, selected, form.fileFormat);
    };

    const importProcess = async (e) => {
        e.preventDefault();
        if (!parsed) {
            toast.dismiss();
            toast.error('Please attach a file to import.');
            return;
        }
        if (!valid?.length) {
            toast.dismiss();
            toast.error('Please upload a file with at least one valid row to import.');
            return;
        }

        let result;
        try {
            result = await importLines(valid, form.importType);
        } catch (error) {
            console.error('Import failed', error);
            setImportLog({ imported: 0, merged: 0, skipped: valid.length, notFound: [], error: `Import failed: ${error?.message || 'unexpected error'}` });
            return;
        }

        // Close only when something actually landed on the form. When nothing did, keep the drawer
        // open with the reason - which Product IDs did not resolve, or why the lookup failed.
        if (result && (result.imported + result.merged) > 0) {
            handleImportAllClose();
        } else {
            setImportLog(result);
        }
    };

    const handleImportAllClose = () => {
        resetImport();
        handleClose();
    };

    return <PrimaryDrawer
        anchor={'right'}
        open={visible}
        variant={'temporary'}
        handleClose={() => handleImportAllClose()}
        title={`Import ${IMPORT_DATA_TYPE}`}
        width={"800px"}
        className={clsx(classes.closeBackGround, classes.width100PImport)}
        component={
            <Box
                className={clsx(classes.customForm, classes.removeUnderlineImport)}
                component="form"
                autoComplete="off"
                onSubmit={(e) => importProcess(e)}
            >
                <div className={clsx(classes.customFormFlex)}>
                    <Row gutter={50}>
                        <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                            <Row>
                                <Col xxl={24} xl={24} lg={24} md={24} sm={24} xs={24}>
                                    <Typography variant={'h6'} className={clsx(classes.typographyHeader, classes.bold)}>Data Type  Selection</Typography>
                                    <Typography variant={'subtitle1'} className={clsx(classes.textFieldLableColorCustom, classes.fonSize13, 'MuiInputLabel-shrink', classes.margin10t)}>Data Type</Typography>
                                    <Typography variant={'h6'} className={clsx(classes.textFieldColor, classes.fonSize14)}>{IMPORT_DATA_TYPE}</Typography>
                                    <AutoCompleteComponent
                                        name={["Import Type"]}
                                        heads={[{ 'key': 'name', 'name': 'Import Type' }]}
                                        details={IMPORT_TYPES}
                                        parentValue={customObject(IMPORT_TYPES, form.importType)}
                                        position={"bottom"}
                                        customClasses={classes.margin20t}
                                        onChange={(data) => changeHandler(data.id, 'importType')}
                                    />
                                    <Typography variant={'h6'} className={clsx(classes.typographyHeader, classes.bold, classes.margin20t)}>Options for Download</Typography>
                                    <AutoCompleteComponent
                                        name={["File Format"]}
                                        heads={[{ 'key': 'name', 'name': 'Data Type' }]}
                                        details={FILE_FORMATS}
                                        parentValue={customObject(FILE_FORMATS, form.fileFormat)}
                                        position={"bottom"}
                                        customClasses={classes.margin10t}
                                        onChange={(data) => changeHandler(data.id, 'fileFormat')}
                                    />
                                    <Button
                                        key="blank"
                                        color="primary"
                                        variant="contained"
                                        className={clsx(classes.AddMoreBtn, classes.padding10, classes.margin35t, classes.Width100Per)}
                                        onClick={() => exportFile('blank')}
                                    >Download Blank Template</Button>
                                    <Button
                                        key="existing"
                                        color="primary"
                                        variant="contained"
                                        className={clsx(classes.AddMoreBtn, classes.padding10, classes.margin35t, classes.Width100Per)}
                                        onClick={() => exportFile('existing')}
                                    >Download With Existing Data</Button>
                                    <Typography variant={'h6'} className={clsx(classes.typographyHeader, classes.bold, classes.margin35t)}>Import</Typography>
                                    <Typography variant={'subtitle1'} className={clsx(classes.textFieldLableColorCustom, 'MuiInputLabel-shrink', classes.fonSize13)}>Browse File</Typography>
                                    <div className={clsx(classes.searchAnimation, classes.iconAnimation, classes.margin10t, classes.Width100Per)}>
                                        <TextField autoComplete='one-time-code'
                                            placeholder='Search'
                                            disabled
                                            value={filePath}
                                            className={clsx(classes.browseFile)}
                                            variant={'standard'}
                                            fullWidth
                                        />
                                        <Image
                                            src={SearchIcon}
                                            preview={false}
                                            onClick={() => fileInput.current.click()}
                                        />
                                        <input
                                            ref={fileInput}
                                            type="file"
                                            accept=".xlsx,.xls,.csv"
                                            onChange={(e) => handleFile(e)}
                                            style={{ display: 'none' }}
                                        />
                                    </div>
                                    <div className={clsx(classes.btnParent)}>
                                        <Space direction="horizontal" align="center" size={[10, 10]}>
                                            <Button
                                                key="attach"
                                                color="primary"
                                                variant="contained"
                                                onClick={() => fileInput.current.click()}
                                                className={clsx(classes.AddMoreBtn)}
                                            >Attach File
                                            </Button>
                                            <Button
                                                key="upload"
                                                color="primary"
                                                variant="contained"
                                                type="submit"
                                                className={clsx(classes.AddMoreBtn)}
                                            >Upload File
                                            </Button>
                                        </Space>
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                        <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                            <Row>
                                <Col xxl={24} xl={24} lg={24} md={24} sm={24} xs={24} className={classes.importExport}>
                                    <Typography variant={'h6'} className={clsx(classes.typographyHeader, classes.bold)}>Data Column Selection</Typography>
                                    <FormControl
                                        className={clsx(classes.dropDownPointer, classes.customRadioButton, classes.margin10t)}
                                        variant={'standard'}
                                        fullWidth>
                                        <RadioGroup
                                            row
                                            aria-labelledby="demo-customized-radios"
                                            name="column"
                                            value={form.column}
                                            onChange={(e) => handleDataColumn(e)}>
                                            <FormControlLabel className={classes.formControlLabel} value="required_fields" control={<Radio
                                                checkedIcon={<UnSelectedIcon />}
                                                icon={<SelectedIcon />}
                                            />} label="Required Fields" />
                                            <FormControlLabel className={classes.formControlLabel} value="all_selected_fields" control={<Radio
                                                checkedIcon={<UnSelectedIcon />}
                                                icon={<SelectedIcon />}
                                            />} label="All Selected Fields" />
                                        </RadioGroup>
                                    </FormControl>
                                    <div
                                        className={clsx(classes.boxBorder, classes.boxBorderImport, classes.margin20t, classes.padding20, classes.fieldList)}>
                                        <Typography variant={'subtitle1'} className={clsx(classes.textFieldLableColorCustom, 'MuiInputLabel-shrink', classes.boxBorderTitle, classes.fonSize13)}>Field List
                                            <Button
                                                key="unselect"
                                                color="primary"
                                                variant="contained"
                                                onClick={() => requiredFields(true)}
                                                className={clsx(classes.unSelectedButton, { [classes.disabled]: form.column === 'required_fields' })}
                                            >Un-Select all
                                            </Button>
                                        </Typography>
                                        <div className={classes.importListFields}>
                                            {
                                                fieldList.map((list, i) => (
                                                    <FormControlLabel key={i}
                                                        className={clsx(classes.formControlLabel)}
                                                        value={list.key}
                                                        control={<Checkbox
                                                            checked={selected.indexOf(list.key) !== -1 ? true : false}
                                                            onChange={(e) => !list?.required && handleChange(e)}
                                                        />}
                                                        label={list.key}
                                                        labelPlacement="end"
                                                    />
                                                ))
                                            }
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    <Row gutter={50}>
                        <Col xxl={24} xl={24} lg={24} md={24} sm={24} xs={24}>
                            {
                                (parseError || parsed) &&
                                <>
                                    <Typography variant={'h6'} className={clsx(classes.typographyHeader, classes.bold, classes.margin30t)}>Logs</Typography>
                                    {
                                        parseError && <CustomAlert error={parseError} customClass={clsx(classes.uploadError, classes.margin10t)} />
                                    }
                                    {
                                        parsed && !parseError && <CustomAlert
                                            success={`${valid.length} row(s) ready to import${rejected.length > 0 ? `, ${rejected.length} row(s) will be skipped` : ''}`}
                                            customClass={clsx(classes.uploadError, classes.margin10t)} />
                                    }
                                    {
                                        parsed && rejected.length > 0 && <div className={clsx(classes.boxBorder, classes.margin10t, classes.padding20)}>
                                            {
                                                rejected.map((r, i) => (
                                                    <p key={i} className={classes.deleteRed}>{`Row ${r.rowNumber}: ${r.reason}`}</p>
                                                ))
                                            }
                                        </div>
                                    }
                                    {
                                        importLog && <div className={clsx(classes.boxBorder, classes.margin10t, classes.padding20)}>
                                            <p className={classes.deleteRed}>{importLog.error || 'No lines were added.'}</p>
                                            {
                                                importLog.notFound?.length > 0 && <p className={classes.deleteRed}>
                                                    {`Product ID not found or not ACTIVE: ${importLog.notFound.join(', ')}`}
                                                </p>
                                            }
                                            {
                                                importLog.skipped > 0 && importLog.notFound?.length === 0 && !importLog.error && <p className={classes.deleteRed}>
                                                    {'Every row matched a product already on the form. Switch Import Type to "Merge Into Existing Lines" to add their quantities.'}
                                                </p>
                                            }
                                        </div>
                                    }
                                </>
                            }
                        </Col>
                    </Row>
                </div>
            </Box>
        }
    />
}
